import React from 'react'
import { useExportTestSession, GetPDFTest, GetPDFTestVariables, Lab } from '@modmd/data'
import { ConfirmationDialog } from 'components/Dialog'
import Button from 'components/Button'
import { Text } from 'components/Typography'
import { useApolloClient } from '@apollo/client'
import { GET_PDF_TEST } from 'sharedComponents/DownloadTestResultPdfButton/operations'
import PDFDocument, {
  PDFDocumentProps,
} from 'sharedComponents/DownloadTestResultPdfButton/PDFDocument'
import { pdf } from '@react-pdf/renderer'

enum Messages {
  VERIFICATION = 'Are you sure to re-export order for this testing session?',
  SUCCESS = 'Order successfully re-exported!',
}

interface Props {
  sessionId: string
  tests?: string[]
}

export const ExportTestSession: React.VFC<Props> = ({ sessionId, tests }) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [exportSession, { loading: isLoadingExportSession }] = useExportTestSession()

  const [isExportSuccessful, setIsExportSuccessful] = React.useState<boolean>(false)

  const handleOpenDialog = () => setIsDialogOpen(true)
  const handleCloseDialog = () => setIsDialogOpen(false)
  const apolloClient = useApolloClient()

  const blobToBase64 = ({ blob, barcode }: { blob: Blob; barcode: string }) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onload = () =>
        resolve(`${barcode}_${reader.result?.toString().split('base64,')?.[1] || ''}`)
      reader.onerror = (error) => reject(error)
    })

  const handleDownloadClick = React.useCallback(
    async (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e?.stopPropagation()
      let base64s
      try {
        if (tests) {
          base64s = await Promise.all(
            tests?.map(async (testId) => {
              const { data: testData } = await apolloClient.query<GetPDFTest, GetPDFTestVariables>({
                query: GET_PDF_TEST,
                variables: { id: testId },
              })
              const { test } = testData
              if (test && test.result) {
                if (
                  test.TestSession.lab === Lab.DendiLax ||
                  test.TestSession.lab === Lab.DendiAtl
                ) {
                  const [participant] = test.ParticipantMembers
                  const data: PDFDocumentProps = {
                    barcode: test.barcode || '-',
                    birthDate: participant.User.birthDate,
                    firstName: participant.User.firstName || '-',
                    lastName: participant.User.lastName || '-',
                    email: participant.User.email || '-',
                    gender: participant.User.gender || '',
                    phoneNumber: participant.User.phoneNumber || '-',
                    id: test.id || '',
                    lab: test.TestSession.lab,
                    result: test.result || '',
                    secondResult: test.secondResult || '',
                    resultReceivedAt: test.resultReceivedAt,
                    specimenType: test.specimenType || 'midNasal',
                    testedAt: test.testedAt,
                    type: test.TestSession.type,
                    Address: {
                      city: participant.User?.Address?.city || '',
                      state: participant.User?.Address?.state || '',
                      street: participant.User?.Address?.street || '',
                      zip: participant.User?.Address?.zip || '',
                    },
                  }

                  const pdfBlob = await pdf(<PDFDocument data={data} />).toBlob()
                  return blobToBase64({ blob: pdfBlob, barcode: test.barcode || '' })
                }
              }
              return ''
            })
          )
        }
        try {
          await exportSession({
            variables: {
              inputData: {
                id: sessionId,
                base64Pdf: (base64s as [string]) || [''],
              },
            },
          })
          setIsExportSuccessful(true)
        } catch {
          // ignore
        }
      } catch {
        // ignore
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId]
  )

  return (
    <>
      <Button onClick={handleOpenDialog}>Re-export order</Button>
      <ConfirmationDialog
        title="Re-export order"
        isOpen={isDialogOpen}
        onDismiss={handleCloseDialog}
        onClose={() => setIsExportSuccessful(false)}
        actions={
          <>
            <Button
              onClick={handleCloseDialog}
              colorVariant={isExportSuccessful ? 'primary' : 'secondary'}
            >
              {isExportSuccessful ? 'Close' : 'Cancel'}
            </Button>
            {!isExportSuccessful && (
              <Button
                isFetching={isLoadingExportSession}
                onClick={async () => {
                  await handleDownloadClick()
                }}
              >
                Submit
              </Button>
            )}
          </>
        }
      >
        <Text>{isExportSuccessful ? Messages.SUCCESS : Messages.VERIFICATION}</Text>
      </ConfirmationDialog>
    </>
  )
}
